<template>
  <div class="week-view w-100 px-3">
    <div class="row mb-4 justify-content-start">
      <div class="col-12 col-md-8 col-lg-6">
        <div class="card p-3 shadow-sm border-0 bg-body-tertiary">
          <div class="row g-2 align-items-center">
            <div class="col-sm-6">
              <label for="dateFrom" class="form-label small fw-bold mb-1 cursor-pointer" @click="openDatePicker('dateFromInput')">Data od</label>
              <input
                  id="dateFrom"
                  ref="dateFromInput"
                  type="date"
                  class="form-control form-control-sm cursor-pointer"
                  v-model="dateFrom"
                  @click="openDatePicker('dateFromInput')"
                  @change="onDateChange"
              />
            </div>
            <div class="col-sm-6">
              <label for="dateTo" class="form-label small fw-bold mb-1 cursor-pointer" @click="openDatePicker('dateToInput')">Data do</label>
              <input
                  id="dateTo"
                  ref="dateToInput"
                  type="date"
                  class="form-control form-control-sm cursor-pointer"
                  v-model="dateTo"
                  :min="dateFrom"
                  @click="openDatePicker('dateToInput')"
                  @change="onDateChange"
              />
            </div>
          </div>
          <div v-if="dateError" class="text-danger small mt-2">
            {{ dateError }}
          </div>
        </div>
      </div>
    </div>

    <div
        v-for="(day, index) in days"
        :key="day.dateStr"
        class="day-section-wrapper d-flex justify-content-center"
    >
      <DaySection
          :index="index"
          :label="day.label"
          :spaces="spaces"
          :time-slots="timeSlots"
          :isLoading="isLoading"
          :cooldown-minutes="cooldownMinutes"
          :external-first-cell="globalFirstCell"
          :external-selected-range="globalSelectedRange"
          :active-section-label="globalActiveSection"
          @update-selection="handleSelectionUpdate"
          @reservation-created="handleReservationCreated"
      />
    </div>
    <FullPageAlert
        v-if="collisionAlert.show"
        :title="collisionAlert.title"
        :message="collisionAlert.message"
        @close="collisionAlert.show = false"
    />
  </div>
</template>

<script>
import DaySection from './DaySection.vue'
import FullPageAlert from "@/components/notifications/FullPageAlert.vue";
import {useAuthStore} from "@/stores/auth";
import {usePricesStore} from "@/stores/prices";
import {fetchWrapper} from "@/wrappers/api/fetchWrapper";

export default {
  name: 'WeekView',
  components: {DaySection, FullPageAlert},
  setup() {
    const authStore = useAuthStore();
    const pricesStore = usePricesStore();
    return {authStore, pricesStore};
  },
  data() {
    return {
      showDays: 14,
      spaces: [],
      startHour: 17,
      endHour: 23,
      cooldownMinutes: 30,
      isLoading: false,
      locationSpaces: [],
      globalFirstCell: null,
      globalSelectedRange: null,
      globalActiveSection: null,
      refreshTimer: null,
      allowGuestSelection: false,
      dateFrom: '',
      dateTo: '',
      dateError: null,
      collisionAlert: {
        show: false,
        title: '',
        message: ''
      }
    }
  },
  computed: {
    formattedPrices() {
      const config = this.pricesStore.getPriceByLocationId(1);
      return config ? config.pricePerHour : null;
    },
    days() {
      const dayNames = ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota']
      const result = []
      const now = new Date()

      let startDate = new Date(now)
      startDate.setHours(0, 0, 0, 0)

      let daysCount = this.showDays

      if (this.dateFrom && this.dateTo && this.dateFrom <= this.dateTo) {
        const [fYear, fMonth, fDay] = this.dateFrom.split('-').map(Number)
        const [tYear, tMonth, tDay] = this.dateTo.split('-').map(Number)

        const fromD = new Date(fYear, fMonth - 1, fDay)
        const toD = new Date(tYear, tMonth - 1, tDay)

        startDate = fromD

        const diffTime = Math.abs(toD - fromD)
        daysCount = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
      }

      // Determine if today should be skipped (if current hour >= endHour) when starting from today
      let startOffset = 0;
      const isDefaultToday = !this.dateFrom && startDate.toDateString() === new Date(now.setHours(0,0,0,0)).toDateString();
      if (isDefaultToday && new Date().getHours() >= this.endHour) {
        startOffset = 1;
      }

      for (let i = startOffset; i < startOffset + daysCount; i++) {
        const date = new Date(startDate)
        date.setDate(startDate.getDate() + i)

        const name = dayNames[date.getDay()]
        const dateStr = date.toLocaleDateString('pl-PL', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })

        const actualToday = new Date()
        const isToday = date.toDateString() === actualToday.toDateString()
        const label = isToday ? `${name}, ${dateStr} (dziś)` : `${name}, ${dateStr}`

        result.push({label, dateStr})
      }

      return result
    },
    timeSlots() {
      const slots = []
      for (let h = this.startHour; h < this.endHour; h++) {
        slots.push(this.formatTime(h, 0))
        slots.push(this.formatTime(h, 30))
      }
      return slots
    },
  },
  methods: {
    formatTime(hour, minute) {
      const hh = String(hour).padStart(2, '0')
      const mm = String(minute).padStart(2, '0')
      return `${hh}:${mm}`
    },
    openDatePicker(refName) {
      const input = this.$refs[refName];
      if (input) {
        if (typeof input.showPicker === 'function') {
          input.showPicker();
        } else {
          input.focus();
        }
      }
    },
    onDateChange() {
      if (this.dateFrom && this.dateTo && this.dateTo < this.dateFrom) {
        this.dateError = 'Data "do" nie może być wcześniejsza niż data "od"';
        return;
      }
      this.dateError = null;
      if (this.dateFrom && this.dateTo) {
        this.loadSpaces();
      }
    },
    async loadSpaces() {
      if (this.isLoading) return;
      if (this.dateFrom && this.dateTo && this.dateTo < this.dateFrom) {
        this.dateError = 'Data "do" nie może być wcześniejsza niż data "od"';
        return;
      }

      const isInitialLoad = this.spaces.length === 0;
      if (isInitialLoad) {
        this.isLoading = true;
      }

      try {
        let locationSpaces = this.locationSpaces;

        // Fetch spaces only if they haven't been loaded yet
        if (isInitialLoad) {
          const spacesData = await fetchWrapper.get('/spaces/location/1');
          this.locationSpaces = spacesData.locationSpaces;
          locationSpaces = this.locationSpaces;
        }

        // Always fetch reservations
        let queryStr = '';
        if (this.dateFrom && this.dateTo) {
          const params = new URLSearchParams({
            from: this.dateFrom,
            to: this.dateTo
          });
          queryStr = `?${params.toString()}`;
        }

        const reservationsData = await fetchWrapper.get(`/admin/reservations/location/1${queryStr}`);
        const allReservations = reservationsData.reservations_data?.reservations || reservationsData.reservations || [];

        // Define updatedSpaces here
        const updatedSpaces = locationSpaces.map(space => {
          const spaceReservations = allReservations.filter(r => (r.SpaceId ?? r.space_id) === space.Id);
          return {
          id: space.Id,
          locationId: space.LocationId,
          name: space.Name,
          description: space.Description,
          active: space.Active,
            reservations: this.groupReservationsByDate(spaceReservations),
            userReservations: {},
          };
        });

        // Update the state
        this.spaces = updatedSpaces;

        // Check for collisions with current selection
        this.checkSelectionCollision();

      } catch (error) {
        //
      } finally {
        this.isLoading = false;
        this.startPolling();
      }
    },
    startPolling() {
      this.stopPolling();
      this.refreshTimer = setTimeout(() => {
        this.loadSpaces();
      }, 100000);
    },
    stopPolling() {
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = null;
      }
    },
    groupReservationsByDate(reservations) {
      return reservations.reduce((acc, reservation) => {
        const startDate = reservation.StartDate || reservation.start_date;
        if (!startDate) return acc;
        const dateKey = startDate.split("T")[0];
        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push(reservation);
        return acc;
      }, {});
    },
    handleSelectionUpdate({label, firstCell, selectedRange}) {
      if (!this.authStore.user && !this.allowGuestSelection && (firstCell || selectedRange)) {
        this.collisionAlert = {
          show: true,
          title: 'Wymagane logowanie',
          message: 'Musisz się zalogować aby zarezerwować stanowisko!'
        };
        return;
      }

      this.globalActiveSection = label;
      this.globalFirstCell = firstCell;
      this.globalSelectedRange = selectedRange;

      // Reset timer on selection interaction to prevent jumpy UI during selection
      this.startPolling();
    },
    checkSelectionCollision() {
      if (!this.globalSelectedRange || !this.globalActiveSection) return;

      const activeSpace = this.spaces[this.globalSelectedRange.spaceIdx];
      if (!activeSpace) return;

      // Extract YYYY-MM-DD from active label
      const dateMatch = this.globalActiveSection.match(/(\d{2})\.(\d{2})\.(\d{4})/);
      if (!dateMatch) return;
      const dateKey = `${dateMatch[3]}-${dateMatch[2]}-${dateMatch[1]}`;

      const dayReservations = activeSpace.reservations[dateKey];
      if (!dayReservations) return;

      // Helper to format slot index to time (matches logic in DaySection)
      const formatTime = (slotIdx) => {
        const h = Math.floor((slotIdx * 30) / 60) + this.startHour;
        const m = (slotIdx * 30) % 60;
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      };

      const rangeStart = formatTime(this.globalSelectedRange.from);
      // End time is start of next slot
      const rangeEnd = formatTime(this.globalSelectedRange.to + 1);

      const isColliding = dayReservations.some(res => {
            const resStart = new Date(res.StartDate || res.start_date);
            const resEnd = new Date(res.EndDate || res.end_date);

        // Apply same cooldown logic for collision detection
        // const startWithCooldown = new Date(resStart.getTime() - this.cooldownMinutes * 60000);
        // const endWithCooldown = new Date(resEnd.getTime() + this.cooldownMinutes * 60000);

        const formatLocal = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
        const s = formatLocal(resStart);
        const e = formatLocal(resEnd);

        // Standard overlap check
        return rangeStart < e && rangeEnd > s;
      });

      if (isColliding) {
        this.handleSelectionUpdate({label: null, firstCell: null, selectedRange: null});
        this.collisionAlert = {
          show: true,
          title: 'Termin zajęty',
          message: 'Niestety ktoś zarezerwował już ten okres! Wybierz ponownie godziny rezerwacji.'
        };
      }
    },
    handleReservationCreated() {
      this.loadSpaces();
    }
  },
  mounted() {
    this.loadSpaces();
  },
  beforeUnmount() {
    this.stopPolling();
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

[data-bs-theme="light"] .bg-pricing {
  background-color: #71a2c0 !important;
}

[data-bs-theme="dark"] .bg-pricing {
  background-color: #3a6580 !important;
}

[data-bs-theme="light"] .bg-price-hour {
  background-color: rgb(184, 168, 131) !important;
  border-color: rgb(120 84 48) !important;
  text-shadow: 0 0 5px black;
}

[data-bs-theme="light"] .bg-price-value {
  background-color: #797868 !important;
}

.week-view {
  background: transparent;
}

.day-section-wrapper {
  width: 100%;
}

.legend-info .slot-user-reserved {
  background-color: rgb(51 106 67) !important;
}
</style>